/**
 * View enum + `renderView()`. Mirrors the shape of the reference's
 * `Foyer_Dashboard_CURRENT.jsx` `App` at line 10048, typed.
 *
 * Phase 1 surfaces: login, verify, sites, addSite, onboarding, mandates,
 * settings, billing, account. Phase 2 surfaces (dashboard, resolution,
 * guardrails, visibility, simulation, benchmarks) all render
 * `<Phase2StubView />` — the navigation exposes them as disabled tabs
 * (`<TopBar />` / `<MobileDrawer />`), but direct routing must still
 * resolve to a real component.
 *
 * Per ADR-0025 §2.1 the `'addSite'` key is now an alias for the
 * Onboarding flow at step 0 — the v1 `AddSite` view has been folded
 * into `DomainStep`. The alias stays in the View union so callers
 * (top-bar, sites list "Add site") keep working; removal is deferred
 * to a follow-up ADR.
 */

import { useEffect } from 'react';

import type { VisitSegmentFilter } from '@sill/types/api';

import { useSites } from './providers.js';
import { useOptionalAuth } from './auth-context.js';
import { Account } from '../views/account/Account.js';
import { Agents } from '../views/agents/Agents.js';
import { AgencyAccessApproval } from '../views/agency-access/AgencyAccessApproval.js';
import { ConnectApproval } from '../views/connect/ConnectApproval.js';
import { AgencyPortal } from '../views/agency-portal/AgencyPortal.js';
import { LEGACY_VIEW_TO_SECTION } from '../views/agency-portal/portal-sections.js';
import { Benchmarks } from '../views/benchmarks/Benchmarks.js';
import { Billing } from '../views/billing/Billing.js';
import { Dashboard } from '../views/dashboard/Dashboard.js';
import { Login } from '../views/login/Login.js';
import { Mandates } from '../views/mandates/Mandates.js';
import { Onboarding } from '../views/onboarding/Onboarding.js';
import { Catalog } from '../views/catalog/Catalog.js';
import { Guardrails } from '../views/guardrails/Guardrails.js';
import { Resolution } from '../views/resolution/Resolution.js';
import { Phase2StubView } from '../views/stub/Phase2StubView.js';
import { SiteShellSkeleton } from '../views/stub/SiteShellSkeleton.js';
import { Settings } from '../views/settings/Settings.js';
import { Simulation } from '../views/simulation/Simulation.js';
import { SitesList } from '../views/sites/SitesList.js';
import { Verify } from '../views/login/Verify.js';
import { Welcome } from '../views/login/Welcome.js';
import { SupportLogin } from '../views/login/SupportLogin.js';
import { Readiness } from '../views/readiness/Readiness.js';
import { Security } from '../views/security/Security.js';

export type View =
  | 'login'
  | 'verify'
  | 'welcome'
  | 'supportLogin'
  | 'sites'
  | 'addSite'
  | 'onboarding'
  | 'mandates'
  | 'settings'
  | 'billing'
  | 'account'
  | 'dashboard'
  | 'resolution'
  | 'guardrails'
  | 'catalog'
  | 'visibility'
  | 'readiness'
  | 'simulation'
  | 'benchmarks'
  | 'agents'
  // ADR founder-approved 2026-10-20 — Security top-level view.
  // Sub-tabs live under `?section=summary|attackers|paths|blocking`.
  | 'security'
  // Agency Portal v2 Slice 4 — unified portal; the five legacy ids below
  // alias to `agencyPortal` with a matching `?section=…`.
  | 'agencyPortal'
  // ADR-0237 Slice 1 (alias → `agencyPortal&section=clients`)
  | 'clients'
  | 'agencyAccessApproval'
  // ADR-0261 — assistant MCP OAuth consent landing (`/connect/:request_id`).
  // Anonymous surface — renders Login in place when signed out and
  // consumes the saved return path on the next authenticated bootstrap.
  | 'connectApproval'
  // ADR-0240 Slice 2a — alias → `agencyPortal&section=referral`
  | 'agencyReferral'
  // ADR-0240 Slice 2b — alias → `agencyPortal&section=reports`
  | 'agencyReports'
  // ADR-0240 Slice 2c — alias → `agencyPortal&section=white-label`
  | 'whiteLabel'
  // ADR-0240 Slice 2d — alias → `agencyPortal&section=widget`
  | 'agencyWidget';

/**
 * Optional deep-link parameters a caller of `setView` can attach to a
 * navigation. The `App` shell encodes the values onto the URL
 * (`?record=…`, `?q=…`) as a one-shot — the destination view consumes
 * them on mount and `setView('…')` away clears them. Mirrors the
 * `?escalation=` pattern that the Resolution view already uses.
 *
 *   - `record`     — open the Mandates view with a specific record_id
 *     pre-selected (the detail drawer opens for that record).
 *   - `q`          — open the Mandates view with the search box pre-filled
 *     (used by the Revenue-by-agent rows on the dashboard, which are
 *     per-agent aggregates with no single record to deep-link to).
 *   - `escalation` — open the Resolution view with a specific
 *     escalation_id pre-selected (the detail panel opens + the list row
 *     scroll-anchors). Mirrors the `?escalation=` deep-link the
 *     escalation-notify email CTA already writes; wiring it through
 *     `setView` lets the dashboard's PendingResolutions rows use the
 *     same one-shot path instead of landing on the auto-selected top row.
 */
export interface ViewOptions {
  record?: string;
  q?: string;
  /** Visits panel: open on this chip (`?segment=`, read once on mount). */
  segment?: VisitSegmentFilter;
  /**
   * Mandates view: open the Suspicious tab AND auto-select this
   * `threat_alert_id` in the detail panel (`?threat=`, read once on
   * mount). The threat alert emails + Slack digests write this when
   * they link to a specific alert — ADR-0245 + founder feedback
   * 2026-10-17.
   */
  threat?: string;
  escalation?: string;
  /** Guardrails pre-fill: open AddRuleModal for this rule type on mount. */
  preset_rule_type?: string;
  /** Guardrails pre-fill: seed the allowlist textarea with this agent_id. */
  preset_agent_id?: string;
  /** Guardrails pre-fill: set the action_on_match selector to reject/escalate. */
  preset_action_on_match?: 'reject' | 'escalate';
  /** Agents view: open on this visitor filter (Dashboard visitor-mix tiles). */
  agent_kind?: 'all' | 'ai' | 'bots' | 'humans';
  /**
   * Security view: open on a specific sub-tab. Written as `?section=`.
   */
  security_section?: 'summary' | 'attackers' | 'paths' | 'blocking' | 'trusted';
  /**
   * Security · Attackers: when opening a specific sender from a deep link
   * (threat alert email, Slack digest). The Mandates sub-view this reuses
   * reads the id from `?threat=` the same way it does from Activity today.
   */
  threat_alert_id?: string;
  /** Settings: deep-link onto a specific tab (`settings_tab`) and
   *  optionally a specific block within it (`settings_section`). The
   *  destination block carries a stable `id={settings_section}` and the
   *  Settings view scrolls it into view + briefly outlines it on mount. */
  settings_tab?: 'general' | 'install' | 'skills' | 'integrations' | 'origins';
  settings_section?: string;
  /** Agents view: pre-select a specific agent key + optional detail tab. */
  agent?: string;
  agents_mode?: 'overview' | 'agents' | 'demand' | 'probes';
}

/**
 * Widened setView signature. Most call sites pass only the view id;
 * the optional second arg is used by deep-linking surfaces (e.g.
 * Dashboard's ActivityFeed / RevenueByAgent rows).
 */
export type SetView = (v: View, opts?: ViewOptions) => void;

// `resolution` graduated out of the Phase-2-disabled set in ADR-0094
// Slice 3 — the HITL escalation review queue ships as a real screen, so
// the TopBar tab activates and the router resolves it below.
export const PHASE_2_VIEWS: ReadonlySet<View> = new Set<View>([]);

export interface ViewRouterProps {
  view: View;
  setView: SetView;
}

export function ViewRouter({ view, setView }: ViewRouterProps): JSX.Element {
  const { activeSite, loaded: sitesLoaded } = useSites();
  const auth = useOptionalAuth();
  const isAuthed = auth?.state.kind === 'authenticated';

  // Founder 2026-07-16: browser Back after signup-confirm lands on the
  // consumed `/welcome` "Create my account" page (the SPA only
  // replaceState's, so it lingers in history). If a session already exists
  // AND no live token is present, forward to onboarding instead of re-showing
  // a completed step.
  //
  // Guard: when a token IS in the URL the visitor may be creating a second
  // account while already signed in to a different one. Skipping the form in
  // that case steals the session without confirmation — the fix is to always
  // show the form when a token is present; the confirm POST will either
  // succeed (minting the new session, overwriting the old cookie) or fail with
  // ?error=invalid_or_expired (consumed Back-button revisit, clear UX).
  const welcomeHasToken =
    view === 'welcome'
    && typeof window !== 'undefined'
    && new URL(window.location.href).searchParams.has('token');
  useEffect(() => {
    if (view === 'welcome' && isAuthed && !welcomeHasToken) setView('onboarding');
  }, [view, isAuthed, welcomeHasToken, setView]);

  // Auth surfaces — outside the site context.
  if (view === 'login') return <Login setView={setView} />;
  if (view === 'verify') return <Verify setView={setView} />;
  // ADR-0185 — new-account confirm interstitial (verify redirects here
  // for unknown emails). An already-authenticated visitor on a Back-button
  // revisit (no live token) renders nothing while the effect above forwards
  // to onboarding. A token-bearing visit always renders the form so a
  // cross-account signup can complete.
  if (view === 'welcome') return (isAuthed && !welcomeHasToken) ? <></> : <Welcome setView={setView} />;
  // ADR-0216 — support-login redeem landing. Runs at /support-login,
  // anonymous (no cookie yet at first paint). The mount reads the raw
  // token from location.hash, scrubs it, POSTs to the API, then
  // reloads to `/` on success.
  if (view === 'supportLogin') return <SupportLogin setView={setView} />;

  // ADR-0237 — agency approval landing is a top-level route (reached
  // via the email link `/settings/agency-access?token=...`). The
  // approval component scrubs the token on mount and gates on an
  // authenticated owner session; a pre-auth landing is routed back
  // to normal sign-in by the component itself.
  if (view === 'agencyAccessApproval') return <AgencyAccessApproval setView={setView} />;

  // ADR-0261 — the `/connect/:request_id` consent landing. Like the
  // agency-access approval page, this is reached from a (possibly
  // signed-out) external URL — the component owns bootstrapping,
  // return-path save, and the signed-out Login-in-place branch.
  if (view === 'connectApproval') return <ConnectApproval setView={setView} />;

  // Account-level views.
  if (view === 'sites') return <SitesList setView={setView} />;
  // Agency Portal v2 Slice 4 — one view, seven sections. Legacy view ids
  // (`clients`, `agencyReports`, `agencyReferral`, `agencyWidget`,
  // `whiteLabel`) alias into the portal; the portal reads `?section=…`
  // from the URL, which `initialView()` rewrites on load so old deep
  // links keep working.
  if (
    view === 'agencyPortal'
    || view === 'clients'
    || view === 'agencyReferral'
    || view === 'agencyReports'
    || view === 'whiteLabel'
    || view === 'agencyWidget'
  ) {
    // Keep merchant site tabs hidden while in the portal (account-level
    // surface; `setActiveSite(null)` makes `inSiteContext` false in the
    // TopBar).
    // Note: no-op if already null; cannot be `useEffect` here because
    // ViewRouter is a plain render function.
    // The portal reads its section from the URL — if the caller
    // navigated with a legacy id, map it to the right section.
    if (view !== 'agencyPortal' && typeof window !== 'undefined') {
      try {
        const url = new URL(window.location.href);
        const section = LEGACY_VIEW_TO_SECTION[view];
        if (section !== undefined && url.searchParams.get('section') === null) {
          url.searchParams.set('section', section);
          window.history.replaceState(null, '', url.toString());
        }
      } catch {
        /* enhancement only */
      }
    }
    return <AgencyPortal />;
  }
  if (view === 'billing') {
    // Legacy standalone billing URL — redirect to Account settings billing tab.
    const url = new URL(window.location.href);
    url.searchParams.set('view', 'account');
    url.searchParams.set('account_tab', 'billing');
    window.history.replaceState(null, '', url.toString());
    return <Account />;
  }
  // ADR-0146 — replaces the inline `AccountStub` placeholder with the
  // real account-settings panel (Profile / Security / Notifications /
  // Billing). The TopBar dropdown's `setView('account')` already
  // routes here; the swap is invisible to navigation.
  if (view === 'account') return <Account />;

  // Onboarding (and its `'addSite'` alias) is intentionally above the
  // activeSite gate — step 0 (`DomainStep`) is where an activeSite is
  // first created, so requiring one to enter would be a chicken-and-egg.
  // Resume mode keys off `activeSite.status === 'pending_verification'`
  // when one happens to be set (e.g. SitesList "Continue setup").
  if (view === 'onboarding' || view === 'addSite') {
    return <Onboarding setView={setView} />;
  }

  // Remaining site-scoped views fall through to a Phase-2 stub if no
  // site is active.
  //
  // Founder 2026-10-21 — while the first `sites.list()` is in flight,
  // `activeSite` is null simply because the fetch hasn't settled. The
  // previous code painted the loud "No active site" splash for ~1
  // round trip on every page load, which read as "you have no sites"
  // to merchants who do. Hold the quiet shell skeleton until the
  // SiteProvider flips `loaded` to true; only THEN fall through to
  // the stub (whose message — "Pick a site from the menu, or add a
  // new one to continue" — is only truthful after a settled load).
  // Treat an absent `loaded` (test providers may build a minimal
  // value) as already-loaded, back-compat with existing scaffolds.
  if (!activeSite) {
    if (sitesLoaded === false) {
      return <SiteShellSkeleton />;
    }
    return <Phase2StubView setView={setView} reason="no_active_site" />;
  }

  // Site-scoped views are KEYED by the active site so switching sites
  // REMOUNTS the view with fresh state. Without the key the mounted
  // component keeps its local state across the switch — the Live Agent
  // view kept the PREVIOUS site's LIVE-tagged responses on screen under
  // the new site's name, and Settings kept stale form state (founder
  // report 2026-07-06). Remount-on-switch fixes the whole class.
  const k = activeSite.site_id;
  if (view === 'dashboard') return <Dashboard key={k} setView={setView} />;
  if (view === 'mandates') return <Mandates key={k} setView={setView} />;
  if (view === 'guardrails') return <Guardrails key={k} />;
  if (view === 'catalog') return <Catalog key={k} />;
  if (view === 'resolution') return <Resolution key={k} />;
  if (view === 'simulation') return <Simulation key={k} />;
  // `visibility` is the legacy nav id; the Agent Readiness page replaces it
  // in-place. The alias keeps deep links (`?view=visibility`) and existing
  // call sites (`goTo('visibility')`) landing on the new page.
  if (view === 'visibility' || view === 'readiness') {
    return <Readiness key={k} setView={setView} />;
  }
  if (view === 'benchmarks') return <Benchmarks key={k} />;
  if (view === 'agents') return <Agents key={k} setView={setView} />;
  if (view === 'security') return <Security key={k} setView={setView} />;
  if (view === 'settings') return <Settings key={k} />;

  if (PHASE_2_VIEWS.has(view)) {
    return <Phase2StubView setView={setView} />;
  }

  return <Phase2StubView setView={setView} />;
}
